/**
 * Minimal Dialog primitive — Phase-1-lean, hand-authored to match the existing
 * button/card/input/label style. Renders a fixed-position panel with a backdrop;
 * Escape key closes; click on backdrop closes; ``open=false`` unmounts.
 *
 * Phase 2+ may swap this out for ``npx shadcn add dialog`` (which would use the
 * unified ``radix-ui`` Dialog primitive — already declared in package.json).
 */
import { useEffect, type ReactNode } from "react";

import { TOKENS } from "@/lib/tokens";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  /** Optional ID used by aria-labelledby. Falls back to a stable static id when title is given. */
  labelId?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  labelId = "dialog-title",
}: DialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.45)",
          zIndex: 60,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? labelId : undefined}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, 92vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: TOKENS.surfaceElevated,
          color: TOKENS.text,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: "12px",
          boxShadow: TOKENS.shadow,
          padding: "24px",
          zIndex: 70,
        }}
      >
        {title && (
          <h2
            id={labelId}
            className="text-lg font-semibold mb-1"
            style={{ color: TOKENS.text }}
          >
            {title}
          </h2>
        )}
        {description && (
          <p className="text-sm mb-4" style={{ color: TOKENS.textMuted }}>
            {description}
          </p>
        )}
        {children}
      </div>
    </>
  );
}
